import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, X, ChevronDown, ChevronUp, PhoneIncoming, CheckCircle, ExternalLink, Calendar, UserCircle } from 'lucide-react';
import { cn } from '../../lib/utils';
import { ghlApi } from '../../lib/ghlApi';
import { toast } from 'sonner';

function useLiveTimer(startedAt) {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!startedAt) return;
    const startMs = new Date(startedAt).getTime();
    if (Number.isNaN(startMs)) return;
    // Clamp to 0 — if the parsed start is in the future (naive tz or
    // clock skew), the caller sees "0:00" instead of "-239:-13".
    const tick = () => setElapsed(Math.max(0, Math.floor((Date.now() - startMs) / 1000)));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [startedAt]);

  const m = Math.floor(elapsed / 60);
  const s = elapsed % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function LiveCallCard({ call, onOptimisticRemove }) {
  // Order matters: `started_at_iso` is always tz-aware (`+00:00`).
  // `started_at` may be a naive UTC string when it came from a Mongo
  // read that stripped the tz. `ts` is the SSE event ts. Any of them is
  // valid *only* if it carries a tz marker; without one the browser
  // interprets it as local time and the timer flips negative.
  const timer = useLiveTimer(call.started_at_iso || call.started_at || call.ts);
  const navigate = useNavigate();
  const isExisting = call.is_existing_patient;
  const juvId = call.juvonno_id || '';
  const ptName = call.pt_name || '';

  const handleForceComplete = () => {
    onOptimisticRemove(call.call_id);
    toast.success('Call marked as completed');
    ghlApi.forceCompleteLiveCall(call.call_id).catch(() => {});
  };

  let juvPortal = '';
  try { juvPortal = JSON.parse(localStorage.getItem('current_location') || '{}').juvonno_portal_url || ''; } catch { /* stale/invalid current_location cache — leave empty */ }

  return (
    <div className="px-4 py-3 border-b border-slate-700/50 last:border-0">
      {/* Row 1: Name + phone + timer */}
      <div className="flex items-center gap-3">
        <div className="relative shrink-0">
          <div className="h-8 w-8 rounded-full bg-amber-500/20 flex items-center justify-center">
            <PhoneIncoming className="h-3.5 w-3.5 text-amber-400" />
          </div>
          <span className="absolute -top-0.5 -right-0.5 flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-amber-500" />
          </span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-white truncate">{call.contact_name || 'Unknown Caller'}</div>
          <div className="text-[11px] text-slate-400">{call.contact_phone || ''}</div>
        </div>
        <div className="font-mono text-sm font-bold text-amber-400 shrink-0">{timer}</div>
      </div>

      {/* Row 2: Patient info + 3 quick action icons */}
      <div className="mt-2 ml-11 flex items-center gap-2">
        {/* Patient info */}
        <div className="flex items-center gap-1.5 flex-1 min-w-0">
          {isExisting ? (
            <span className="text-[9px] font-bold bg-emerald-500/20 text-emerald-400 px-1.5 py-0.5 rounded shrink-0">Existing</span>
          ) : (
            <span className="text-[9px] font-bold bg-amber-500/20 text-amber-400 px-1.5 py-0.5 rounded shrink-0">New</span>
          )}
          {ptName && <span className="text-[10px] text-blue-400 truncate">PT: {ptName}</span>}
        </div>

        {/* 3 Quick action icons */}
        <div className="flex items-center gap-1 shrink-0">
          {/* 1. Juvonno Profile — only for existing patients */}
          {isExisting && juvId && juvPortal ? (
            <a
              href={`${juvPortal}/patient/profile.php?txtCustomerID=${juvId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="h-7 w-7 rounded-lg bg-indigo-500/20 hover:bg-indigo-500/40 flex items-center justify-center transition-colors"
              title="Juvonno Profile"
              onClick={e => e.stopPropagation()}
            >
              <UserCircle className="h-3.5 w-3.5 text-indigo-400" />
            </a>
          ) : (
            <div className="h-7 w-7 rounded-lg bg-slate-700/30 flex items-center justify-center opacity-30" title="No Juvonno ID">
              <UserCircle className="h-3.5 w-3.5 text-slate-500" />
            </div>
          )}

          {/* 2. Book Appointment — preselect contact */}
          <button
            onClick={() => navigate(`/scheduling?bookContact=${call.contact_id || ''}&name=${encodeURIComponent(call.contact_name || '')}`)}
            className="h-7 w-7 rounded-lg bg-emerald-500/20 hover:bg-emerald-500/40 flex items-center justify-center transition-colors"
            title="Book Appointment"
          >
            <Calendar className="h-3.5 w-3.5 text-emerald-400" />
          </button>

          {/* 3. Mark Completed */}
          <button
            onClick={handleForceComplete}
            className="h-7 w-7 rounded-lg bg-red-500/20 hover:bg-red-500/40 flex items-center justify-center transition-colors"
            title="Mark Completed"
          >
            <CheckCircle className="h-3.5 w-3.5 text-red-400" />
          </button>
        </div>
      </div>
    </div>
  );
}

export function LiveCallOverlay({ liveCalls = [] }) {
  const [minimized, setMinimized] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [hiddenIds, setHiddenIds] = useState(new Set());
  const prevCountRef = useRef(0);

  const handleOptimisticRemove = (callId) => {
    setHiddenIds(prev => new Set(prev).add(callId));
  };

  const visibleCalls = liveCalls.filter(c => !hiddenIds.has(c.call_id));

  // Clear hidden IDs when liveCalls changes (SSE confirmed removal)
  useEffect(() => {
    const currentIds = new Set(liveCalls.map(c => c.call_id));
    setHiddenIds(prev => {
      const next = new Set();
      for (const id of prev) {
        if (currentIds.has(id)) next.add(id);
      }
      return next;
    });
  }, [liveCalls]);

  // Auto-show panel again when a new call comes in
  useEffect(() => {
    if (visibleCalls.length > prevCountRef.current) {
      setDismissed(false);
      setMinimized(false);
    }
    prevCountRef.current = visibleCalls.length;
  }, [visibleCalls.length]);

  if (visibleCalls.length === 0 || dismissed) return null;

  return (
    <div
      className={cn(
        'fixed bottom-5 right-5 z-50 w-80 rounded-2xl overflow-hidden shadow-2xl',
        'border border-slate-700/80',
        'bg-slate-900/95 backdrop-blur-xl'
      )}
      data-testid="live-call-overlay"
      style={{ transition: 'all 0.2s ease' }}
    >
      {/* Header bar */}
      <div className="flex items-center justify-between px-4 py-2.5 bg-slate-800/80 border-b border-slate-700/50">
        <div className="flex items-center gap-2">
          <span className="relative flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-amber-500" />
          </span>
          <span className="text-xs font-bold text-white">
            {visibleCalls.length} Call{visibleCalls.length > 1 ? 's' : ''} in Progress
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setMinimized(m => !m)}
            className="h-6 w-6 rounded-full hover:bg-slate-700 flex items-center justify-center text-slate-400 hover:text-white transition-colors"
            title={minimized ? 'Expand' : 'Minimize'}
          >
            {minimized ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
          </button>
          <button
            onClick={() => setDismissed(true)}
            className="h-6 w-6 rounded-full hover:bg-slate-700 flex items-center justify-center text-slate-400 hover:text-white transition-colors"
            title="Dismiss"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {/* Call cards */}
      {!minimized && (
        <div className="max-h-72 overflow-y-auto">
          {visibleCalls.map(call => (
            <LiveCallCard key={call.call_id} call={call} onOptimisticRemove={handleOptimisticRemove} />
          ))}
        </div>
      )}
    </div>
  );
}
